import { useLoginInfo } from '@/components/LoginInfo/provider';
import { useApMismatchAlertSettings } from '@/context/services/ap_mismatch_alert_settings/ApMismatchAlertSettings.service';
import { toEnabledTargetKeys } from '@/context/services/ap_mismatch_alert_settings/utils';
import { useGetApPayeeAccountNumber } from '@/context/services/ap_payee/ApPayee.service';
import {
  getInferredApPayeeId,
  isInferredPayeeAccountNumberVerified,
  toApReportInferencesMap,
  toSourceOnlyEntry,
  type ApReportInference,
  type ApReportInferencesMap,
} from '@/context/services/report_inferences/utils';
import { useDataExtractionMismatchAlertFlag } from '@/hooks/feature_flag/useDataExtractionMismatchAlertFlag';
import { useDataExtractionMismatchAlertPhase2Flag } from '@/hooks/feature_flag/useDataExtractionMismatchAlertPhase2Flag';
import { RoleEnum } from 'ap-graphql';
import { useCallback, useMemo } from 'react';

type UseMismatchAlertReturn = {
  inferencesMap: ApReportInferencesMap;
  isFieldAlertEnabled: (targetKey: string) => boolean;
};

export const useMismatchAlert = (
  inferences: ApReportInference[],
  reportTypeId: string
): UseMismatchAlertReturn => {
  const isMismatchAlertFlag = useDataExtractionMismatchAlertFlag();
  const isMismatchAlertPhase2Flag = useDataExtractionMismatchAlertPhase2Flag();
  const { currentOffice, currentOfficeMember } = useLoginInfo();
  const shouldSkipForMaskedAccountNumber =
    currentOffice?.apOfficeSetting?.isAccountNumberMasked &&
    !(currentOfficeMember?.officeMemberSetting?.role === RoleEnum.Admin);

  const settings = useApMismatchAlertSettings(isMismatchAlertFlag);

  const enabledTargetKeys = useMemo(
    () => toEnabledTargetKeys(settings.data ?? [], reportTypeId),
    [settings.data, reportTypeId]
  );

  const isFieldAlertEnabled = useCallback(
    (targetKey: string): boolean =>
      isMismatchAlertFlag && enabledTargetKeys.has(targetKey),
    [isMismatchAlertFlag, enabledTargetKeys]
  );

  const registeredAccountNumber = useGetApPayeeAccountNumber(
    isMismatchAlertFlag &&
      !isMismatchAlertPhase2Flag &&
      !shouldSkipForMaskedAccountNumber
      ? getInferredApPayeeId(inferences)
      : undefined
  );

  const inferencesMap = useMemo(() => {
    const map = toApReportInferencesMap(inferences);

    // Payee bank name / branch comparison is Phase 2 only.
    if (!isMismatchAlertPhase2Flag) {
      map.payeeBankName = undefined;
      map.payeeBankBranch = undefined;
    }

    // Masking applies to the account number only. Bank name and branch stay
    // comparable so a mismatch can still be shown.
    if (shouldSkipForMaskedAccountNumber) {
      map.apPayee = undefined;
    }

    // Phase 1 only: the OCR-read account number is trusted only when it matches
    // the account number registered on the inferred payee (inference.ap_payee).
    // Otherwise there is nothing reliable to compare, so the alert is skipped.
    // Phase 2 compares the OCR-read values as-is, so this gate is dropped.
    if (
      !isMismatchAlertPhase2Flag &&
      !isInferredPayeeAccountNumberVerified(
        registeredAccountNumber,
        map.apPayee?.value
      )
    ) {
      map.apPayee = undefined;
      map.payeeBankName = undefined;
      map.payeeBankBranch = undefined;
    }

    // when source_detail is returned while the flag is off, drop the breakdown to maintain phase 1 wording
    if (!isMismatchAlertPhase2Flag) {
      map.apPayee = toSourceOnlyEntry(map.apPayee);
      map.dueDate = toSourceOnlyEntry(map.dueDate);
      map.invoiceRegistrationNumber = toSourceOnlyEntry(
        map.invoiceRegistrationNumber
      );
      map.totalAmount = toSourceOnlyEntry(map.totalAmount);
    }

    return map;
  }, [
    inferences,
    isMismatchAlertPhase2Flag,
    shouldSkipForMaskedAccountNumber,
    registeredAccountNumber,
  ]);

  return { inferencesMap, isFieldAlertEnabled };
};
